import React from 'react'
import { Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useCreateCouponMutation, useUpdateCouponMutation } from '../../../../../services/cellcast.v2'
import { CouponPayload } from '../../../../../services/cellcast.v2.types'
import {toast} from 'react-toastify'
import { Spinner } from 'react-bootstrap'

const validationSchema = Yup.object().shape({
  code: Yup.string().required('Coupon code is required'),
  description: Yup.string().nullable(),
  staffId: Yup.string().required('Staff is required'),
  commisionRate: Yup.number()
    .required('Commision Rate is required')
    .min(0, 'Commision Rate cannot be negative')
    .max(100, 'Commision Rate cannot be more than 100'),
  minCredit: Yup.number()
    .required('Min Credit is required')
    .min(1, 'Min Credit must be at least 1'),
  maxCredit: Yup.number()
    .required('Max Credit is required')
    .min(1, 'Max Credit must be at least 1')
    .test(
      'max-greater-than-min',
      'Max Credit must be greater than or equal to Min Credit',
      function (value) {
        const { minCredit } = this.parent
        if (value == null || minCredit == null) return true
        return value >= minCredit
      }
    ),
  type: Yup.string().oneOf(['sms', 'mms']).required('Type is required'),
  discountType: Yup.string()
    .oneOf(['percentage', 'fixed_price', 'free_credit'])
    .required('Discount type is required'),
  percentageOff: Yup.number()
    .when('discountType', {
      is: 'percentage',
      then: (schema) => schema.required('Percentage is required').min(1).max(100),
      otherwise: (schema) => schema.notRequired(),
    }),
  flatRate: Yup.number().when('discountType', {
    is: 'fixed_price',
    then: (schema) => schema.required('Flat rate is required').min(0.001),
    otherwise: (schema) => schema.notRequired(),
  }),
  freeCredit: Yup.number().when('discountType', {
    is: 'free_credit',
    then: (schema) => schema.required('Free credit is required').min(1),
    otherwise: (schema) => schema.notRequired(),
  }),
  status: Yup.boolean().default(true),
  expiredDay: Yup.number().required('Expired Day is required'),
})

const CreateCoupon = ({ show, handleClose, edit, onFetch }: any) => {
  const stafflist = useSelector((state: any) => state?.StaffAccount?.staffAccountList)
  const [createCoupon, {isLoading}] = useCreateCouponMutation()
  const [updateCoupon, {isLoading: updateLoading}] = useUpdateCouponMutation()

  const handleSubmit = async (values: any) => {
    try {

        if (isLoading || updateLoading) return;

        const cleanedValues = Object.fromEntries(
          Object.entries(values).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
        ) as CouponPayload;

        let response;
        if (!edit?.code) {
          response = await createCoupon({ payload: cleanedValues }).unwrap()
        } else {
          response = await updateCoupon({id: edit?._id, payload: cleanedValues}).unwrap()
        }

        if (response?.status) {
          toast.success(`Success ${edit?.code ? "edit" : "create"} coupon`)
          onFetch()
          handleClose();
        }
      } catch (error: any) {
        toast.error(error.data?.message || 'Failed to create or update coupon')
      }
  }

  return (
    <Modal
      id='kt_modal_create_coupon'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      onHide={handleClose}
    >
      <div className='modal-header'>
        <h2>{edit?.code ? "Edit Coupon" : "Create Coupon"}</h2>
      </div>
      <Formik
        initialValues={{
          code: edit?.code || '',
          description: edit?.description || '',
          type: edit?.type || 'sms',
          discountType: edit?.discountType || '',
          percentageOff: edit?.percentageOff || '',
          flatRate: edit?.flatRate || '',
          freeCredit: edit?.freeCredit || '',
          status: edit?.status,
          maxCredit: edit?.maxCredit || 0,
          minCredit: edit?.minCredit || 0,
          commisionRate: edit?.commisionRate || 0,
          staffId: edit?.staffId?._id || '',
          expiredDay: edit?.expiredDay || ''
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className='modal-body px-20'>
            <div className='row justify-content-center'>
              {/* Coupon Code */}
              <div className='mb-3'>
                <label className='col-form-label required fw-bold fs-6'>Coupon Code</label>
                <Field
                  name='code'
                  className='form-control-solid form-control'
                  placeholder='Coupon Code'
                />
                <ErrorMessage name='code' component='span' className='text-danger' />
              </div>

              {/* Description */}
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Description</label>
                <Field
                  as='textarea'
                  name='description'
                  className='form-control-solid form-control'
                  placeholder='Coupon Description'
                />
              </div>

              <div className='d-flex gap-4 w-100 mb-3'>
                <div className='flex-grow-1'>
                  <label className='col-form-label required fw-bold fs-6'>Staff</label>
                  <Field as='select' name='staffId' className='form-select form-select-solid'>
                    <option value=''>-- Select Staff --</option>
                    {stafflist?.items?.map((item: any) => (
                      <option value={item?._id}>{item?.name}</option>
                    ))}
                  </Field>
                  <ErrorMessage name='staffId' component='span' className='text-danger' />
                </div>

                <div className='flex-grow-1'>
                  <label className='col-form-label required fw-bold fs-6'>Commision Rate</label>
                  <Field
                    name='commisionRate'
                    type='number'
                    className='form-control-solid form-control'
                    placeholder='Enter commision rate'
                  />
                  <ErrorMessage name='commisionRate' component='span' className='text-danger' />
                </div>
              </div>

              {/* Type */}
              <div className='mb-3'>
                <label className='col-form-label required fw-bold fs-6'>Type</label>
                <Field as='select' name='type' className='form-select form-select-solid'>
                  <option value='sms'>SMS</option>
                  <option value='mms'>MMS</option>
                </Field>
                <ErrorMessage name='type' component='span' className='text-danger' />
              </div>

              <div className='d-flex gap-4 mb-4 w-100'>
                {/* Discount Type */}
                <div className='flex-grow-1'>
                  <label className='col-form-label required fw-bold fs-6'>Discount Type</label>
                  <Field as='select' name='discountType' className='form-select form-select-solid'>
                    <option value=''>-- Select Type --</option>
                    <option value='percentage'>Percentage</option>
                    <option value='fixed_price'>Flat Rate</option>
                  </Field>
                  <ErrorMessage name='discountType' component='span' className='text-danger' />
                </div>

                {/* Conditional Fields */}
                {values.discountType === 'percentage' && (
                  <div className='flex-grow-1'>
                    <label className='col-form-label required fw-bold fs-6'>Percentage</label>
                    <Field
                      name='percentageOff'
                      type='number'
                      className='form-control-solid form-control'
                      placeholder='Enter percentage'
                    />
                    <ErrorMessage name='percentageOff' component='span' className='text-danger' />
                  </div>
                )}

                {values.discountType === 'fixed_price' && (
                  <div className='flex-grow-1'>
                    <label className='col-form-label required fw-bold fs-6'>Flat Rate ($)</label>
                    <Field
                      name='flatRate'
                      type='number'
                      className='form-control-solid form-control'
                      placeholder='Enter flat rate'
                    />
                    <ErrorMessage name='flatRate' component='span' className='text-danger' />
                  </div>
                )}

                {values.discountType === 'free_credit' && (
                  <div className='flex-grow-1'>
                    <label className='col-form-label required fw-bold fs-6'>Free Credit</label>
                    <Field
                      name='freeCredit'
                      type='number'
                      className='form-control-solid form-control'
                      placeholder='Enter free credit'
                    />
                    <ErrorMessage name='freeCredit' component='span' className='text-danger' />
                  </div>
                )}
              </div>

              <div className='d-flex gap-4 w-100'>
                <div className='flex-grow-1'>
                  <label className='col-form-label required fw-bold fs-6'>Min Credit</label>
                  <Field
                    name='minCredit'
                    type='number'
                    className='form-control-solid form-control'
                    placeholder='Enter min credit'
                  />
                </div>
                <div className='flex-grow-1'>
                  <label className='col-form-label required fw-bold fs-6'>Max Credit</label>
                  <Field
                    name='maxCredit'
                    type='number'
                    className='form-control-solid form-control'
                    placeholder='Enter max credit'
                  />
                </div>
              </div>
               <div className='mb-3'>
                <ErrorMessage name='minCredit' component='span' className='text-danger' />
                <ErrorMessage name='maxCredit' component='span' className='text-danger' />
               </div>
              
              <div className='mb-3'>
                <label className='col-form-label required fw-bold fs-6'>Expired Day</label>
                <Field
                  name='expiredDay'
                  type='number'
                  className='form-control-solid form-control'
                  placeholder='Enter expired day'
                />
                <ErrorMessage name='expiredDay' component='span' className='text-danger' />
              </div>
              {/* Status Toggle */}
              <div className='mb-3'>
                <div className='col-lg-8 d-flex align-items-center mt-5'>
                  <div className='form-check form-check-solid form-switch fv-row'>
                    <Field
                      type='checkbox'
                      name='status'
                      className='form-check-input w-45px h-30px'
                      checked={values.status}
                      onChange={() => setFieldValue('status', !values.status)}
                    />
                  </div>
                  <label className='form-check-label ms-3 fw-bold'>
                    {values.status ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
            </div>

            <div className='d-flex justify-content-end mt-3'>
              <button
                type='button'
                onClick={handleClose}
                className='btn btn-md fs-7 gray_bg_purple me-2'
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn-sm btn-light btn-primary fw-500 w-100px p-3 border border-primary'
              >
                {isLoading || updateLoading ? <span className='indicator-label'>Submiting</span> : <span className='indicator-label'>Submit</span>}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default CreateCoupon
